import { useEffect, useMemo } from 'react';
import { AvatarProps } from '@frontend/design-system';
import { usePhotoStore } from './use-photo-store';

/**
 * Enriches an array of AvatarProps with profile photos fetched from the photo store.
 *
 * For each avatar that has a `userId` (personId), this hook:
 * 1. Triggers a batch photo fetch via `handleGetPictures` (no-ops for IDs already fetched or in-flight)
 * 2. Returns a new array with `src` populated from the photo record
 */
export const useAvatarsWithPhotos = (avatars: AvatarProps[]): AvatarProps[] => {
  const handleGetPictures = usePhotoStore((state) => state.handleGetPictures);
  const photoRecord = usePhotoStore((state) => state.photoRecord);

  // Derive a stable primitive key from userIds so the effect only re-runs
  // when the actual set of users changes — not on every render due to an
  // unstable avatars array reference from the caller.
  const userIdsKey = avatars
    .map((a) => a.userId)
    .filter(Boolean)
    .sort()
    .join(',');

  useEffect(() => {
    const ids = userIdsKey.split(',').filter(Boolean);
    if (ids.length > 0) handleGetPictures(ids);
  }, [userIdsKey, handleGetPictures]);

  // Derive a stable primitive from the resolved photo values for these specific
  // users. Combined with userIdsKey, this lets the memo skip recomputation when
  // neither the user set nor their photos have changed — even when the caller
  // passes an unstable avatars array reference on every render.
  const photosKey = avatars.map((a) => (a.userId ? photoRecord[a.userId] ?? '' : '')).join('|');

  return useMemo(
    () =>
      avatars.map((avatar) => {
        if (!avatar.userId) return avatar;
        const photo = photoRecord[avatar.userId];
        // Only override src when a non-empty photo URL is available so that the
        // initials / icon fallback in Avatar is preserved for contacts without photos.
        return photo ? { ...avatar, src: photo } : avatar;
      }),
    // Keyed on stable primitives rather than unstable array/object refs:
    // userIdsKey captures which users; photosKey captures their resolved photos.
    [userIdsKey, photosKey]
  );
};
